import { Ref, useMemo, useState, type FC } from "react";
import { Grid, Group, Stack, UnstyledButton } from "@mantine/core";
import Text from "@/components/atoms/Text";
import { useTranslation } from "react-i18next";
import { getContractTradeType, getTradingPartnerCompany } from "@/utils/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { LocationAndDeliveryFormInput, getLocationAndDeliverySchema } from "../validation/locationAndDelivery";
import { formatAddress } from "@/utils/AddressHelper";
import TextInput from "@/components/atoms/TextInput";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";
import Divider from "@/components/atoms/Divider";
import SelectProductBoxType from "@/components/molecules/SelectInputs/SelectProductBoxType";
import SelectShippingAddress from "@/components/molecules/SelectInputs/SelectShippingAddress";
import DatePicker from "@/components/molecules/DatePicker";
import CoordinateFields from "@/components/molecules/CoordinateFields";
import { Contract } from "@/types";
import {
  determineIfEstimatedDeliveryIsRequired,
  getContractDeliveryAddresses,
} from "@/components/organisms/CreateOrderFromContract/helpers";
import { useGetCompanyById, useGetCompanyByIdPublic } from "@/services/Company";
import DeliverySlotPicker from "@/components/organisms/DeliverySlotPicker";
import dayjs from "dayjs";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  contract: Contract;
  ref?: Ref<HTMLFormElement>;
}

const LocationAndDeliveryStep: FC<Props & { ref?: React.Ref<HTMLFormElement> }> = ({
  onValidationSuccess,
  onChangeWizardData,
  contract,
  ref,
}) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  const [slotPickerOpen, setSlotPickerOpen] = useState(false);
  const [showCounterpartAddressSelect, setShowCounterpartAddressSelect] = useState(false);

  const { data: company } = useGetCompanyById(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });

  const contractTradeType = getContractTradeType(contract!, currentCompany);
  const counterpartCompanyId = getTradingPartnerCompany(contract, currentCompany?.companyId)?.id;

  // The receiver (the company that has the reception) is always the buyer
  const receiverCompanyId = contract?.contractBuyers[0]?.id;
  const fractionId = contract?.contractProduct?.companySpecificProduct?.salesItemId ?? null;

  const { data: receiverCompany } = useGetCompanyByIdPublic(receiverCompanyId as number, {
    enabled: !!receiverCompanyId,
  });

  const receiverHasOrderPlanning = receiverCompany?.isOrderPlanningEnabled ?? false;

  const isDeliveryTimeRequired = determineIfEstimatedDeliveryIsRequired(
    company?.isOrderPlanningEnabled ?? true,
    contract.automaticSalesApproval
  );

  const { ownAddress, counterpartAddress } = useMemo(
    () => getContractDeliveryAddresses(contract, contractTradeType),
    [contract, contractTradeType]
  );

  const contractAddressDefaults = useMemo(
    () => ({
      address: ownAddress?.address ?? "",
      postCode: ownAddress?.postCode ?? "",
      countryId: ownAddress?.country.id,
      place: ownAddress?.place ?? "",
    }),
    [ownAddress]
  );

  const { handleSubmit, control, watch, setValue } = useForm<LocationAndDeliveryFormInput>({
    resolver: zodResolver(getLocationAndDeliverySchema(isDeliveryTimeRequired, receiverHasOrderPlanning)),
    defaultValues: {
      boxtypeId: 1,
      differentAddress: false,
      address: contractAddressDefaults.address,
      postCode: contractAddressDefaults.postCode,
      countryId: contractAddressDefaults.countryId,
      place: contractAddressDefaults.place,
      estimatedDelivery: undefined,
      latitude: "",
      longitude: "",
      capturedAt: null,
      receptionSlotId: null,
      selectedCounterpartAddress: null,
    },
  });

  const onSubmit = async (data: LocationAndDeliveryFormInput) => {
    onChangeWizardData(data);
    onValidationSuccess(data);
  };

  const toggleCounterpartAddressSelect = () => {
    // Hiding the select drops the pick, so an address the user can no longer see is never sent.
    if (showCounterpartAddressSelect) setValue("selectedCounterpartAddress", null);
    setShowCounterpartAddressSelect(!showCounterpartAddressSelect);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack justify="flex-start">
            {/* YOUR COMPANY */}
            <Group p={0} gap={0} bg="transparent" align="center">
              <Text color="secondary" size="sm" data-testid={"company-deliver-label"}>
                {contractTradeType === ContractTradeTypeEnum.BUY
                  ? t("contracts:deliverTo")
                  : t("contracts:deliverFrom")}
              </Text>
              <ChangeAddressLink
                onClick={() => setValue("differentAddress", !watch("differentAddress"))}
                testId={"change-address-btn"}
              />
            </Group>
            {ownAddress && (
              <Text size="lg" strong={true} data-testid={"company-deliver-address"}>
                {formatAddress(ownAddress)}
              </Text>
            )}
            {watch("differentAddress") && (
              <SelectShippingAddress
                companyId={currentCompany?.companyId}
                label={t("common:shippingAddress")}
                placeholder={t("common:shippingAddress")}
                autoSelectDefault={false}
                data-testid={"company-shipping-address-select"}
                setSelectedAddress={(address) => {
                  setValue("address", address.address, { shouldValidate: true, shouldDirty: true });
                  setValue("postCode", address.postCode, { shouldValidate: true, shouldDirty: true });
                  setValue("place", address.place, { shouldValidate: true, shouldDirty: true });
                  setValue("countryId", address.country.id, { shouldValidate: true, shouldDirty: true });
                }}
              />
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          {/* THEIR COMPANY */}
          <Stack>
            <Group p={0} gap={0} bg="transparent" align="center">
              <Text color="secondary" size="sm" data-testid={"counterpart-deliver-label"}>
                {contractTradeType === ContractTradeTypeEnum.BUY
                  ? t("contracts:deliverFrom")
                  : t("contracts:deliverTo")}
              </Text>
              <ChangeAddressLink onClick={toggleCounterpartAddressSelect} testId={"counterpart-change-address-btn"} />
            </Group>
            {counterpartAddress && (
              <Text size="lg" strong={true} data-testid={"counterpart-deliver-address"}>
                {formatAddress(counterpartAddress)}
              </Text>
            )}
            {showCounterpartAddressSelect && (
              <SelectShippingAddress
                companyId={counterpartCompanyId}
                label={t("common:shippingAddress")}
                placeholder={t("common:shippingAddress")}
                autoSelectDefault={false}
                data-testid={"counterpart-shipping-address-select"}
                setSelectedAddress={(address) => setValue("selectedCounterpartAddress", address)}
              />
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="address"
            render={({ field, fieldState }) => (
              <TextInput
                data-testid={"address-input"}
                label={t("orders:orderAddress")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="countryId"
            render={({ field: { onChange, value }, fieldState }) => (
              <SelectCountry
                labelKey="name"
                valueKey="id"
                data-testid={"country-select"}
                label={t("common:country")}
                disabled={!watch("differentAddress")}
                value={value?.toString()}
                onChange={(val) => onChange(val ? Number(val) : null)}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="postCode"
            render={({ field, fieldState }) => (
              <TextInput
                id="postCode-order-from-contract"
                data-testid={"postCode-input"}
                label={t("orders:postCode")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="place"
            render={({ field, fieldState }) => (
              <TextInput
                id="postPlace"
                data-testid={"place-input"}
                label={t("orders:place")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <CoordinateFields
            control={control}
            latitudeName="latitude"
            longitudeName="longitude"
            setValue={setValue}
            onCaptured={(capturedAt) => setValue("capturedAt", capturedAt)}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="boxtypeId"
            render={({ field: { onChange, value }, fieldState }) => (
              <SelectProductBoxType
                id={"boxType-select"}
                labelKey="description"
                valueKey="id"
                data-testid={"boxType-select"}
                label={t("product:packaging")}
                // Packaging is always required, so the chosen option can't be clicked away
                allowDeselect={false}
                value={value != null ? String(value) : null}
                onChange={(val) => onChange(val ? Number(val) : null)}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="estimatedDelivery"
            render={({ field: { onChange, value }, fieldState }) => {
              return (
                <DatePicker
                  testId="estimated-delivery"
                  label={t("contracts:estimatedDelivery")}
                  required={isDeliveryTimeRequired && !receiverHasOrderPlanning}
                  disabled={!contract.automaticSalesApproval || receiverHasOrderPlanning}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  value={value ?? null}
                  onChange={onChange}
                  showTimePicker
                />
              );
            }}
          />
        </Grid.Col>

        {receiverHasOrderPlanning && (
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
        )}

        {receiverHasOrderPlanning && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="receptionSlotId"
              render={({ field: { value }, fieldState }) => (
                <TextInput
                  label={t("orders:deliveryTimeSlot")}
                  required
                  readOnly
                  value={
                    value
                      ? (() => {
                          const deliveryDate = watch("estimatedDelivery");
                          return deliveryDate
                            ? dayjs(deliveryDate as Date).format("DD.MM.YYYY HH:mm")
                            : t("orders:chooseDeliveryTimeSlot");
                        })()
                      : t("orders:chooseDeliveryTimeSlot")
                  }
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  onClick={() => setSlotPickerOpen(true)}
                  styles={{ input: { cursor: "pointer" } }}
                  data-testid="delivery-time-slot-input"
                />
              )}
            />
          </Grid.Col>
        )}
      </Grid>

      {receiverHasOrderPlanning && (
        <DeliverySlotPicker
          opened={slotPickerOpen}
          receiverCompanyId={receiverCompanyId as number}
          fractionId={fractionId}
          onClose={() => setSlotPickerOpen(false)}
          onConfirm={(slot) => {
            setValue("receptionSlotId", slot.id);
            setValue("estimatedDelivery", new Date(`${slot.slotDate}T${slot.startTime}`));
            setSlotPickerOpen(false);
          }}
        />
      )}
    </form>
  );
};

export default LocationAndDeliveryStep;

const ChangeAddressLink = ({ onClick, testId }: { onClick: () => void; testId: string }) => {
  const { t } = useTranslation();

  return (
    <UnstyledButton onClick={onClick} data-testid={testId}>
      <Text
        ml={10}
        color="secondary"
        size="xs"
        td="underline"
        style={{
          cursor: "pointer",
          color: "var(--color-text-secondary)",
        }}
        data-testid={`${testId}-text`}
      >
        {t("actions:changeAddress")}
      </Text>
    </UnstyledButton>
  );
};
